import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export interface GroupSelectionDialogData {
  title: string;
  groups: { label: string; group: any }[];
  /** When true, show draft name input field */
  showDraftName?: boolean;
  /** Label for the draft name input */
  draftNameLabel?: string;
  /** Placeholder for the draft name input */
  draftNamePlaceholder?: string;
  /** Validation message when name is empty */
  draftNameRequired?: string;
  /** Default value for the draft name */
  defaultDraftName?: string;
  /** When true, show scope radio buttons (e.g., "Current page" vs "All pages") */
  showScopeChoice?: boolean;
  /** Label for the scope choice section */
  scopeLabel?: string;
  /** Options for the scope choice */
  scopeOptions?: { value: string; label: string }[];
  /** Default selected scope value */
  defaultScope?: string;
}

@Component({
  selector: 'app-group-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    FormsModule,
    TranslateModule
  ],
  template: `
    <div class="group-selection-dialog">
      <div class="dialog-icon">
        <mat-icon>content_copy</mat-icon>
      </div>

      <h2 class="title">{{ data.title }}</h2>

      @if (data.groups.length > 0) {
        <mat-radio-group [(ngModel)]="selectedIndex" class="group-list">
          @for (option of data.groups; track $index) {
            <label class="group-option" [class.selected]="selectedIndex() === $index" (click)="selectedIndex.set($index)">
              <mat-radio-button [value]="$index">
                {{ option.label }}
              </mat-radio-button>
            </label>
          }
        </mat-radio-group>
      }

      @if (data.showDraftName) {
        <div class="draft-name-section">
          <label class="draft-name-label">{{ data.draftNameLabel }}</label>
          <input
            type="text"
            class="draft-name-input"
            [(ngModel)]="draftName"
            [placeholder]="data.draftNamePlaceholder || ''"
            (keydown.enter)="onConfirm()"
          />
          @if (showNameError()) {
            <span class="draft-name-error">{{ data.draftNameRequired }}</span>
          }
        </div>
      }

      @if (data.showScopeChoice && data.scopeOptions) {
        <div class="scope-section">
          <label class="draft-name-label">{{ data.scopeLabel }}</label>
          <div class="scope-options">
            @for (opt of data.scopeOptions; track opt.value) {
              <label class="scope-option" [class.selected]="selectedScope === opt.value" (click)="selectedScope = opt.value">
                <mat-radio-button [checked]="selectedScope === opt.value" [value]="opt.value">
                  {{ opt.label }}
                </mat-radio-button>
              </label>
            }
          </div>
        </div>
      }

      <div class="actions">
        <button class="btn-cancel" (click)="onCancel()">
          <mat-icon>close</mat-icon>
          {{ 'COMMON.CANCEL' | translate }}
        </button>
        <button class="btn-confirm" (click)="onConfirm()" [disabled]="!canConfirm()">
          <mat-icon>check</mat-icon>
          {{ 'COMMON.CONFIRM' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .group-selection-dialog {
      padding: 28px 32px;
      min-width: 380px;
      max-width: 450px;
      text-align: center;
    }

    .dialog-icon {
      width: 56px;
      height: 56px;
      margin: 0 auto 20px;
      background: #e8f0eb;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        font-size: 28px;
        width: 28px;
        height: 28px;
        color: #3c7056;
      }
    }

    .title {
      margin: 0 0 16px 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #3b3b3b;
    }

    .group-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 24px;
      text-align: left;
      // Orders with many products must not push the name input and the
      // action buttons off-screen — the option list scrolls, the rest stays.
      max-height: 40vh;
      overflow-y: auto;
      padding-right: 4px;

      &::-webkit-scrollbar {
        width: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: #cfd8d2;
        border-radius: 3px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
    }

    .group-option {
      display: block;
      padding: 12px 16px;
      border: 1.5px solid #eaeaea;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #3c7056;
        background: #f8faf8;
      }

      &.selected {
        border-color: #3c7056;
        background: #e8f0eb;
      }
    }

    .draft-name-section {
      text-align: left;
      margin-bottom: 24px;
    }

    .draft-name-label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: #5a6a5a;
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .draft-name-input {
      width: 100%;
      padding: 12px 16px;
      border: 1.5px solid #eaeaea;
      border-radius: 10px;
      font-size: 14px;
      color: #3b3b3b;
      outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &:focus {
        border-color: #3c7056;
      }

      &::placeholder {
        color: #bbb;
      }
    }

    .draft-name-error {
      display: block;
      font-size: 12px;
      color: #d32f2f;
      margin-top: 6px;
    }

    .scope-section {
      text-align: left;
      margin-bottom: 24px;
    }

    .scope-options {
      display: flex;
      gap: 8px;
    }

    .scope-option {
      flex: 1;
      display: block;
      padding: 10px 14px;
      border: 1.5px solid #eaeaea;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #3c7056;
        background: #f8faf8;
      }

      &.selected {
        border-color: #3c7056;
        background: #e8f0eb;
      }
    }

    .actions {
      display: flex;
      justify-content: center;
      gap: 12px;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      min-width: 130px;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-cancel {
      background: white;
      border: 1.5px solid #eaeaea;
      color: #5a6a5a;

      &:hover {
        background: #f8faf8;
        border-color: #d4ddd4;
      }
    }

    .btn-confirm {
      background: linear-gradient(135deg, #3c7056 0%, #2f5a45 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(60, 112, 86, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(60, 112, 86, 0.4);
      }
    }
  `]
})
export class GroupSelectionDialogComponent {
  readonly dialogRef = inject(MatDialogRef<GroupSelectionDialogComponent>);
  readonly data: GroupSelectionDialogData = inject(MAT_DIALOG_DATA);

  selectedIndex = signal<number | null>(null);
  draftName = this.data.defaultDraftName || '';
  selectedScope = this.data.defaultScope || (this.data.scopeOptions?.[0]?.value ?? '');
  showNameError = signal(false);

  canConfirm(): boolean {
    // If groups exist, one must be selected
    if (this.data.groups.length > 0 && this.selectedIndex() === null) return false;
    // If name field is shown, it must not be empty
    if (this.data.showDraftName && !this.draftName.trim()) return false;
    return true;
  }

  onConfirm(): void {
    if (this.data.showDraftName && !this.draftName.trim()) {
      this.showNameError.set(true);
      return;
    }

    const result: any = { draftName: this.draftName.trim() };
    if (this.data.showScopeChoice) {
      result.scope = this.selectedScope;
    }

    if (this.data.groups.length > 0) {
      const idx = this.selectedIndex();
      if (idx !== null && this.data.groups[idx]) {
        this.dialogRef.close({ ...this.data.groups[idx], ...result });
      }
    } else {
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
